
// @ts-ignore
const indexedDB = typeof window !== 'undefined' ? (window.indexedDB || window.webkitIndexedDB || window.mozIndexedDB || window.OIndexedDB || window.msIndexedDB) : null,
    // @ts-ignore
    IDBTransaction : IDBTransaction = typeof window !== 'undefined' ? (window.IDBTransaction || window.webkitIDBTransaction || window.OIDBTransaction || window.msIDBTransaction) : null,
    dbVersion = 1.0;

export class IndexedDBLoader {
    protected db : IDBDatabase;
    readonly name: string;

    private _onInitialized?: Function;
    
    constructor(name: string, db?: IDBDatabase) {
        // @ts-ignore
        this.db = db;
        this.name = name;
    }

    public static async Init(name: string, db?: IDBDatabase) : Promise<IndexedDBLoader> {
        const loader = new IndexedDBLoader(name, db);
        await loader.initialize();
        return loader;
    }

    public async awaitInitialized(timeout=5000) {
        if(this.initialized) {
            return true;
        }
 
        return new Promise((resolve, reject) => {
            const timeoutTimer = setTimeout(() => {
                return reject(`Timed out awaiting indexeddb initialization`);
            })
            this._onInitialized = () => {
                clearTimeout(timeoutTimer);
                return resolve(true);
            }
        });
    }

    get initialized() : boolean {
        return !!this.db;
    }

    public async initialize() : Promise<boolean> {
        if(this.db) {
            this._onInitialized?.();
            return true;
        };
        const request = indexedDB.open(this.name, dbVersion);
        return new Promise((resolve, reject) => {
            request.onerror = function (event) {
                console.error("Error creating/accessing IndexedDB database", event);
                return reject("Error creating/accessing IndexedDB database");
            };

            request.onsuccess = (event) => {
                this.db = request.result;
                this.db.onerror = function (event) {
                    console.error("Error creating/accessing IndexedDB database", event);
                    return reject(`Error creating/accessing IndexedDB database`)
                };

                // Interim solution for Google Chrome to create an objectStore. Will be deprecated
                // @ts-ignore
                if (this.db.setVersion) {
                    if (this.db.version != dbVersion) {
                        // @ts-ignore
                        const setVersion = this.db.setVersion(dbVersion);
                        setVersion.onsuccess = function () {
                            this.db.createObjectStore(this.name);
                            this._onInitialized?.();
                            return resolve(true);
                        };
                    }
                    else {
                        this.db.createObjectStore(this.name);
                        this._onInitialized?.();
                        return resolve(true);
                    }
                } else {
                    this._onInitialized?.();
                    return resolve(true);
                }
            }

            // For future use. Currently only in latest Firefox versions
            request.onupgradeneeded = (event) => {
                // @ts-ignore
                this.db = event.target.result;
                this.db.createObjectStore(this.name);
            };
        })
    }

    public async deleteItem(name: string) : Promise<boolean> {
        const transaction = this.db.transaction([this.name], 'readwrite');
        const request = transaction.objectStore(this.name).delete(name);
        return new Promise((resolve, reject) => {
            request.onsuccess = (event: Event) => {
                return resolve(true)
            };
            request.onerror = (event: Event) => {
                console.warn(`Error deleting item from indexedDB: ${name}`)
                return resolve(false);
            };
        });
    }
    


    public async saveBlob(name: string, data: Blob) : Promise<boolean> {
        const transaction = this.db.transaction([this.name], 'readwrite');
        const request = transaction.objectStore(this.name).put(data, name);
        return new Promise((resolve, reject) => {
            request.onsuccess = (event: Event) => {
                return resolve(true)
            };
            request.onerror = (event: Event) => {
                return reject(`Error saving blob to indexedDB: ${name}`)
            };
        });
    }

    public async loadBlob(name: string, decode?: (blob: Blob) => any) : Promise<Blob | null> {
        const transaction = this.db.transaction([this.name], 'readonly');
        const request = transaction.objectStore(this.name).get(name)
        return new Promise((resolve, reject) => {
            request.onsuccess = (event: Event) => {
                try {
                    if(decode) {
                        // @ts-ignore
                        decode(event.target.result).then(res => {
                            return resolve(res)
                        })
                    } else {
                        // @ts-ignore
                        return resolve(event.target.result);
                    }
                } catch (err) {
                    return reject(`Error loading blob ${name} from db: ${this.name}. Error: ${err.message}`);
                }
            };
            request.onerror = (event: Event) => {
                return reject(`Error on request loading blob ${name} from db: ${this.name}`);
            };
        });
    }

    
    public async loadRaw (name: string) : Promise<string | null> {
        const transaction = this.db.transaction([this.name], 'readonly');
        const request = transaction.objectStore(this.name).get(name)
        return new Promise((resolve, reject) => {
            request.onsuccess = async (event: Event) => {
                // @ts-ignore
                return resolve(event.target.result);
            };
            request.onerror = (event: Event) => {
                return reject(`Error on request getting raw data from indexedDB: ${name}`);
            };
        });
    }

    public async saveJson(name: string, data: any) : Promise<boolean> {
        const transaction = this.db.transaction([this.name], 'readwrite');
        const request = transaction.objectStore(this.name).put(JSON.stringify(data), name);
        return new Promise((resolve, reject) => {
            request.onsuccess = (event: Event) => {
                return resolve(true)
            };
            request.onerror = (event: Event) => {
                return reject(`Error saving texture to indexedDB: ${name}`)
            };
        });
    }

    public async loadJson(name: string, dontParse=false) : Promise<any> {
        const transaction = this.db.transaction([this.name], 'readonly');
        const request = transaction.objectStore(this.name).get(name)
        return new Promise((resolve, reject) => {
            request.onsuccess = (event: Event) => {
                try {
                    // @ts-ignore
                    const res = dontParse ? event.target.result : JSON.parse(event.target.result);
                    return resolve(res);
                } catch (err) {
                    return resolve(null)
                }
            };
            request.onerror = (event: Event) => {
                return reject(Error(`Error getting texture from indexedDB: ${name}`));
            };
        });
        return null;
    }

    public async saveImageData(name: string, dataUrl: Blob | string) : Promise<boolean> {
        const transaction = this.db.transaction([this.name], 'readwrite');
        const request = transaction.objectStore(this.name).put(dataUrl, name);
        return new Promise((resolve, reject) => {
            request.onsuccess = (event: Event) => {
                return resolve(true)
            };
            request.onerror = (event: Event) => {
                return reject(`Error saving texture to indexedDB: ${name}`)
            };
        });
    }

    public async loadImageData(name: string) : Promise<Blob | string> {
        const transaction = this.db.transaction([this.name], 'readonly');
        const request = transaction.objectStore(this.name).get(name)
        return new Promise((resolve, reject) => {
            request.onsuccess = (event: Event) => {
                // @ts-ignore
                const imgFile = event.target.result;
                return resolve(imgFile);
            };
            request.onerror = (event: Event) => {
                return reject(`Error getting texture from indexedDB: ${name}`);
            };
        });
    }
    
    public async getAllKeysThatStartWith(startWith: string) : Promise<Array<string>> {
        const transaction = this.db.transaction([this.name], 'readonly');
        const objectStore = transaction.objectStore(this.name);
        const request = objectStore.getAllKeys();

        return new Promise((resolve, reject) => {
            request.onsuccess = async (event: Event) => {
                // @ts-ignore
                const keys = event.target.result;
                if(!keys)return resolve([]);
                const filtered = keys.filter(k => k.startsWith(startWith));
                return resolve(filtered);
            };
            request.onerror = (event: Event) => {
                return reject(`Error getting all keys from indexedDB that startWith: ${startWith}`);
            };
        });
    }
    public async saveRaw(name, data: string) : Promise<boolean> {
        return this.saveBlob(name, data as unknown as Blob);
    }
}